import React, { useEffect, useImperativeHandle, useMemo, useState, } from 'react';
import { applyOverrides as applyRuleOverrides } from '@wix/form-conditions';
import { formView as formViewEvent } from '@wix/bi-logger-forms-platform-uou/v2';
import { FieldActionsContext, FieldValidationContext, Form, useBi, } from '@wix/form-fields';
import { deepEqual } from 'fast-equals';
import { useFormValues } from './use-form-values';
import { useFormView } from './use-form-view';
import { useReportFieldEdit } from './use-report-field-edit';
import { useSubmit } from './use-submit';
import { reportErrorChanges, useValidation } from './use-validation';
import { diff } from '../../services/utils';
import { applyNestedForms } from '../../services';
import { applyFormOverrides } from '../../services/form-overrides';
import { DATA_HOOKS } from '../../tpa/widget/data-hooks';
import { useScrollToFirstError } from './use-scroll-to-first-error';
import { applyNestedFormOverrides } from '@wix/nested-form-overrides';
import { useAppLoadEnded } from '../../hooks/use-app-loaded';
export const FormRoot = ({ form: unprocessedForm, // renaming form to prevent accidental use
values: unprocessedValues, // renaming values to prevent accidental use
stepId, onChange, errors, onValidate, onSubmit, enableScrollToFirstError = true, forwardedRef, readOnly, style, overrides, actions, inProgress, }) => {
    // `values` – we are working with the full list for all steps.
    // `errors` – working only with the errors list for the fields inside current step.
    const [currentStep, setCurrentStep] = useState(0);
    const { appLoadEnded } = useAppLoadEnded(actions);
    const formWithOverrides = useMemo(() => applyNestedForms(applyFormOverrides(applyNestedFormOverrides(applyRuleOverrides(unprocessedForm, unprocessedValues)), overrides !== null && overrides !== void 0 ? overrides : {})), [unprocessedForm, unprocessedValues, overrides]);
    const normalizedValues = useFormValues(formWithOverrides, unprocessedValues, onChange);
    const bi = useBi();
    const reportFieldEdit = useReportFieldEdit(formWithOverrides);
    const _onChange = (newValues) => {
        if (!deepEqual(normalizedValues, newValues)) {
            reportFieldEdit(diff(normalizedValues, newValues));
            onChange(newValues);
        }
    };
    const _onValidate = (newErrors) => {
        reportErrorChanges({
            bi,
            existingErrors: errors,
            newErrors,
            form: formWithOverrides,
        });
        onValidate(newErrors);
    };
    useEffect(() => {
        bi.report(formViewEvent({ mode: readOnly ? 'preview' : 'live_site' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const formViews = useFormView(formWithOverrides, stepId, style === null || style === void 0 ? void 0 : style.styleParams);
    const { validateFields, validateStep, handleFieldValidation } = useValidation({
        form: formWithOverrides,
        values: normalizedValues,
        errors,
        setErrors: _onValidate,
    });
    const { scrollToFirstError, handleScrollToFirstError } = useScrollToFirstError({
        form: formWithOverrides,
        options: { enableScrollToFirstError },
        setCurrentStep,
    });
    const { submitForm } = useSubmit({
        values: normalizedValues,
        validateFields,
        handleScrollToFirstError,
        onSubmit,
    });
    useImperativeHandle(forwardedRef, () => ({
        validate: async () => {
            const validationErrors = await validateFields();
            if (validationErrors && validationErrors.length > 0) {
                if (enableScrollToFirstError) {
                    scrollToFirstError();
                }
                return false;
            }
            return true;
        },
        validateStep,
    }), [
        validateStep,
        validateFields,
        enableScrollToFirstError,
        scrollToFirstError,
    ]);
    const setNextStep = () => setCurrentStep(currentStep + 1);
    const setPreviousStep = () => setCurrentStep(currentStep - 1);
    const fieldActions = useMemo(() => actions
        ? {
            actions: Object.assign(Object.assign({}, actions), { submitForm, setNextStep, setPreviousStep }),
            formId: formWithOverrides.id,
            inProgress,
        }
        : { actions: { submitForm, setNextStep, setPreviousStep } }, [actions, formWithOverrides.id, inProgress, submitForm]);
    appLoadEnded();
    if (!(formViews === null || formViews === void 0 ? void 0 : formViews.length)) {
        return null;
    }
    return (React.createElement(FieldValidationContext.Provider, { value: {
            validateFields: handleFieldValidation,
        } },
        React.createElement(FieldActionsContext.Provider, { value: fieldActions },
            React.createElement("div", { "data-hook": DATA_HOOKS.FORM_ROOT },
                React.createElement(Form, { form: formViews[currentStep], values: normalizedValues, onChange: _onChange, errors: errors })))));
};
//# sourceMappingURL=form-root.js.map